<script lang="ts">
	import { resolveAny } from '$lib/shared/lib';
	interface Props {
		variant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'error' | 'surface';
		style?: 'filled' | 'soft' | 'outline' | 'ghost' | 'ringed' | 'glass';
		size?: 'sm' | 'base' | 'lg' | 'xl';
		type?: 'button' | 'submit' | 'reset';
		href?: string;
		width?: string;
		disabled?: boolean;
		loading?: boolean;
		icon?: boolean;
		onclick?: (() => void) | undefined;
		classes?: string;
		title?: string;
		children?: import('svelte').Snippet;
	}

	let {
		variant = 'primary',
		style = 'filled',
		size = 'base',
		type = 'button',
		href = '',
		width = '',
		disabled = false,
		loading = false,
		icon = false,
		onclick = undefined,
		classes = '',
		title = '',
		children
	}: Props = $props();

	const variantTextClasses: Record<typeof variant, string> = {
		primary: '',
		secondary: '',
		tertiary: '',
		success: '',
		warning: '!text-surface-900',
		error: '',
		surface: ''
	};

	function handleClick() {
		if (onclick && !disabled && !loading) {
			onclick();
		}
	}
	// Map (style, variant) to Skeleton 4 preset class names per the codemod mapping
	// in 02-PATTERNS.md (lines 706–709): filled→preset-filled-{v}-500,
	// soft|ghost→preset-tonal-{v}, outline|ringed→preset-outlined-{v}-500,
	// glass→preset-glass-{v}.
	let variantClass = $derived(
		(() => {
			switch (style) {
				case 'filled':
					return `preset-filled-${variant}-500`;
				case 'soft':
				case 'ghost':
					return `preset-tonal-${variant}`;
				case 'outline':
				case 'ringed':
					return `preset-outlined-${variant}-500`;
				case 'glass':
					return `preset-glass-${variant}`;
				default:
					return `preset-filled-${variant}-500`;
			}
		})()
	);
	let buttonClass = $derived(icon ? 'btn-icon' : 'btn');
	let sizeClass = $derived(size === 'base' ? '' : `${buttonClass}-${size}`);
	let textClass = $derived(variantTextClasses[variant]);
	let buttonClasses = $derived(
		`${buttonClass} rounded-lg ${variantClass} ${sizeClass} ${width} ${textClass} ${classes}`.trim()
	);
</script>

{#if href}
	<a
		href={resolveAny(href)}
		class={buttonClasses}
		aria-disabled={disabled}
		class:opacity-50={disabled}
		class:cursor-not-allowed={disabled}
		role="button"
		{title}
	>
		{#if loading}
			<div class="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
		{/if}
		{@render children?.()}
	</a>
{:else}
	<button
		{type}
		class={buttonClasses}
		{disabled}
		class:opacity-50={loading || disabled}
		class:cursor-wait={loading}
		aria-disabled={disabled}
		class:cursor-not-allowed={disabled}
		onclick={handleClick}
		{title}
	>
		{#if loading}
			<div class="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
		{/if}
		{@render children?.()}
	</button>
{/if}
